import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ThemeProvider } from 'react-css-themr';
import { savePhaseItem, deletePhaseItem } from 'actionCreators/phase_items';
import { can } from '../../../../../../../../services';
import { getRole } from '../../../../../../../../reducers';
import LineItemTable from 'components/LineItemTable';
import LaborSheetRow from 'laborSheetRow';
import LaborSheetType from 'types/laborSheet';
import ContractorLaborTables from '../components/contractorLaborTables';
import documentType from 'types/documentUpload';
import laborTableStyles from './laborTables.scss';

const LINE_ITEM_TYPE = 'laborSheets';
const theme = { CollapsableCard: laborTableStyles };

class LaborSheetTable extends Component {
  constructor(props) {
    super(props);
    this.state = { laborSheetIds: [], isInEditMode: false };
  }

  componentDidMount = () => (this.props.showLineItems ?
    this.toggleLaborSheetLineItems(this.getAllLaborSheetIds(), true) :
    null);

  componentDidUpdate = (prevProps, prevState) => {
    const { showLineItems, tableName, toggleShowHide } = this.props;
    const { laborSheetIds } = this.state;
    const allLabSheetIds = this.getAllLaborSheetIds();
    if (prevProps !== this.props && showLineItems &&
      (laborSheetIds.length !== allLabSheetIds.length)) {
      this.toggleLaborSheetLineItems(allLabSheetIds, true);
    }
    if (prevProps.showLineItems !== showLineItems) {
      if (showLineItems) this.toggleLaborSheetLineItems(allLabSheetIds, true);
      if (!showLineItems) this.toggleLaborSheetLineItems([], true);
    }
    if (prevState.laborSheetIds.length !== laborSheetIds.length &&
        laborSheetIds.length === 0) {
      toggleShowHide(tableName, false);
    } else if (prevState.laborSheetIds.length !== laborSheetIds.length &&
               laborSheetIds.length === allLabSheetIds.length) {
      toggleShowHide(tableName, true);
    }
  }

  getAllLaborSheetIds = () => {
    const newLabSheetIds = [];
    this.props.lineItems.forEach((li) => {
      if (li.id > 0) newLabSheetIds.push(li.id);
    });
    return newLabSheetIds;
  }

  handleSave = lineItem => this.props.savePhaseItem(this.props.phaseId, lineItem, LINE_ITEM_TYPE);

  canUpdate = (role, item, lpa) => {
    if (lpa || can(role, 'update', 'labor_sheet', { obj_status: item.status })) return true;
    return false;
  }

  determineCanViewTotal = role => can(role, 'view', 'labor_total');

  toggleLaborSheetLineItems = (labSheetIds, toggleAll = false) => {
    const { laborSheetIds } = this.state;
    const newLabSheetIdsArr = laborSheetIds.slice();
    if (toggleAll) {
      // labSheetIds is multiple ID's
      // add all line items to laborSheetIds
      this.setState({ laborSheetIds: labSheetIds });
    } else {
      // labSheetIds are individual ID's
      // add single line Item to laborSheetIds
      if (labSheetIds >= 0 && !newLabSheetIdsArr.includes(labSheetIds)) {
        newLabSheetIdsArr.push(labSheetIds);
        this.toggleLPATutorialStep2(labSheetIds);
      } else {
        // remove single line item from laborSheetIds
        const index = newLabSheetIdsArr.indexOf(labSheetIds);
        newLabSheetIdsArr.splice(index, 1);
      }
      this.setState({ laborSheetIds: newLabSheetIdsArr });
    }
  }

  toggleLPATutorialStep2 = (labSheetId) => {
    const { lineItems = [] } = this.props;
    const lineItem = lineItems.find(item => item.id === labSheetId) || {};
    if (['Pending Review', 'Not Payable'].includes(lineItem.status)) this.props.doLPATutorialStep2(true);
  }

  toggleEditMode = bool => this.setState({ isInEditMode: bool });

  render = () => {
    const { lineItems, documents, refreshLineItemList, phaseId,
            editable, lpa, role, employableType, filterLineItems } = this.props;
    const { laborSheetIds, isInEditMode } = this.state;
    const total = lineItems.reduce((curr, next) => curr + (next.total || 0), 0);
    const canViewTotal = this.determineCanViewTotal(role);
    return (<ThemeProvider theme={theme}>
      <LineItemTable
        hasCreatableRow={false}
        readOnly={!editable}
        title="Labor Sheets"
        refreshList={refreshLineItemList}
        total={total}
        canViewTotal={canViewTotal}
        toggleEditMode={this.toggleEditMode}
        diffChildren
        accountingTable
      >
        {lineItems.filter(li => li.id > 0).map((item, index) => (
          <div key={item.id}>
            <LaborSheetRow
              key={item.id}
              role={role}
              onSave={this.handleSave}
              lineItem={item}
              documents={documents}
              index={index + 1}
              phaseId={phaseId}
              readOnly={!this.canUpdate(role, item)}
              canViewTotal={canViewTotal}
              toggleLineItem={isInEditMode ? null : () => this.toggleLaborSheetLineItems(item.id)}
              phase={this.props.phase}
            />
            {(laborSheetIds.includes(item.id) && can(role, 'view', 'contractor_line_item')) &&
              <ContractorLaborTables
                readOnly={!this.canUpdate(role, item, lpa)}
                phaseId={phaseId}
                lineItemId={item.id}
                employableType={employableType}
                refreshLineItemList={refreshLineItemList}
                appointmentStartDate={item.appointment.start_date}
                filterLineItems={filterLineItems}
              />
            }
          </div>
        ))}
      </LineItemTable>
    </ThemeProvider>);
  }
}

LaborSheetTable.defaultProps = { lineItems: [] };

LaborSheetTable.propTypes = {
  phaseId: PropTypes.string,
  employableType: PropTypes.string,
  tableName: PropTypes.string,
  toggleShowHide: PropTypes.func,
  savePhaseItem: PropTypes.func,
  deletePhaseItem: PropTypes.func,
  requestPhaseItems: PropTypes.func,
  refreshLineItemList: PropTypes.func.isRequired,
  lineItems: PropTypes.arrayOf(LaborSheetType),
  documents: PropTypes.arrayOf(documentType).isRequired,
  editable: PropTypes.bool.isRequired,
  doLPATutorialStep2: PropTypes.func,
  lpa: PropTypes.bool,
  showLineItems: PropTypes.bool,
  role: PropTypes.string,
  filterLineItems: PropTypes.bool,
  phase: PropTypes.object,
};

export default connect(state => ({
  editable: can(getRole(state), 'editable', 'labor_sheet'),
  lpa: can(getRole(state), 'update', 'LPA'),
  role: getRole(state),
  phase: state.phase.details,
}), { savePhaseItem, deletePhaseItem })(LaborSheetTable);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/tabs/phaseAccounting/components/laborSheetTable.jsx